import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  createBrowserRouter,
  RouterProvider,
  Route,
  Link,
  Outlet,
  createRoutesFromElements,
  useLocation,
  useNavigate,
  Navigate
} from "react-router-dom";
import NotFound from "./routes/NotFound.js"
import Outbox from "./routes/Outbox";
import Home from "./routes/Home";
import Draft from "./routes/Draft.js";
import Favourite from "./routes/Favourite.js";
import Navbar from "./components/Navbar";
import Signup from "./routes/Signup";
import WalletConnect from "./routes/WalletConnect.js";
import "./App.css";
import Header from "./components/Header";
import CommonLayout from "./components/CommonLayout.js";
import Sent from "./routes/Sent.js";
// import TourTooltip from "./components/TourTooltip.js";
import { requireAuth } from "./authMiddleware.js";
import Settings from "./routes/Settings.js";
import Folders from "./routes/Folders.js";
import { Provider } from "react-redux";
import store from "./store.js";
import Trash from "./routes/Trash.js";
import MobileSetting from "./routes/MobileSetting.js";
import Insecure from "./routes/Insecure.js";
import Archive from "./routes/Archive.js";
// import CalendarComponent from "./routes/Calendar.js";
// import CalendarHeader from "./components/CalendarHeader.js";
import "./assets/css/calendar.css";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Notification from "./components/Notification.js";
import 'bootstrap/dist/css/bootstrap.min.css';
import { getProfileSettings } from "./helper/settingsHelper.js";
import { useDispatch } from "react-redux";
import NoInternetPage from "./components/NoInternet.js";
import SignIn from "./routes/SignIn.js";
import ConfirmSendAs from "./routes/ConfirmSendAs";
import SecureMail from "./routes/SecureMail.js";
import { reportError, toErrorString } from "./utils/errorLogger.js";
import VersionLabel from "./components/VersionLabel.js";
import { ConfigProvider } from "antd";
import { installComposeBufferScrub } from "./helper/composeBuffer.js";
import ErrorBoundary from "./components/ErrorBoundary.js";

// The compose window mirrors the mail body + recipients into localStorage in the clear as the user types.
// Scrub it at boot and on unload so it cannot outlive the session — see helper/composeBuffer.js for why
// this buffer is cleared rather than sealed under the DEK (short version: nothing ever reads it back after
// a page load, so the persisted copy is residue with no reader).
installComposeBufferScrub();

// dMail design tokens — Montserrat + the refined brand green, applied to every antd component.
const dmailTheme = {
  token: {
    colorPrimary: "#12B76A",
    colorInfo: "#12B76A",
    colorSuccess: "#12B76A",
    colorLink: "#0B6DE0",
    colorLinkHover: "#0B63CC",
    colorTextBase: "#131717",
    borderRadius: 10,
    fontFamily:
      "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  components: {
    Button: { controlHeight: 40, fontWeight: 600, primaryShadow: "none" },
    Input: { controlHeight: 40 },
    Select: { controlHeight: 40 },
  },
};

// const HeaderWrapper = ({ children }) => {
//   const location = useLocation();
//   const isCalendarPage = location.pathname === '/calendar';
//   return (
//     <>
//       {!isCalendarPage && <Header />}
//       {isCalendarPage && <CalendarHeader />}
//       {children}
//     </>
//   );
// };

const handleToggleSidebar = () => {
  document.body.classList.toggle('leftsidebar-toggle-menu');
};

const AppLayout = () => {
  const dispatch = useDispatch();
  async function getsettings() {
    try {
      const response = await getProfileSettings();
      if (response) {
        dispatch({ type: "PROFILE_NAME", payload: response })
      }
    } catch (err) {
      const errorStr = toErrorString(err || err.message);
      reportError({
        endpoint: '-',
        message: `FileName: index.js, Fun: AppLayout, ${String(err.message || 'Unhandled error')}`,
        error: errorStr,
        userId: window?.currentUser?.id || '',
      });
      console.error(err);
    }
  }
  useEffect(() => {
    getsettings();
  }, []);

  return (
    <>
      <NoInternetPage />
      <Navbar />
      <div className="parent-component-div-align">
        <Header toggleSidebar={handleToggleSidebar} />
        <div className="body-component-container">
          <Outlet />
          <ToastContainer pauseOnFocusLoss={false} />
          <Notification />
          {/* <TourTooltip /> */}
        </div>
      </div>
      <VersionLabel />
    </>)
};

const InitialLayout = () => (
  <>
    <div className="parent-component-div-initial-layout">
      <Outlet />
      <CommonLayout />
      <ToastContainer pauseOnFocusLoss={false} />
      {/* <TourTooltip /> */}
    </div>
    <VersionLabel />
  </>
);

const withoutLayout = () => {
  return (
    <>
      <Outlet />
      <ToastContainer pauseOnFocusLoss={false} />
      {/* <TourTooltip /> */}
      <VersionLabel />
    </>
  )
}

const pathArray = [
  { path: "/inbox", element: <Home /> },
  { path: "outbox", element: <Outbox /> },
  { path: "draft", element: <Draft /> },
  { path: "important", element: <Favourite /> },
  { path: "sent", element: <Sent /> },
  { path: "settings", element: <Settings /> },
  { path: "folders", element: <Folders /> },
  { path: "trash", element: <Trash /> },
  { path: "insecure", element: <Insecure /> },
  { path: "archive", element: <Archive /> },
  { path: "*", element: <NotFound /> },
  { path: "mobilesetting", element: <MobileSetting /> },
  //{ path: "calendar", element: <CalendarComponent /> },
]


const router = createBrowserRouter([
  {
    element: <AppLayout />,
    loader: requireAuth,
    children: pathArray,
  },
  {
    element: withoutLayout(),
    loader: requireAuth,
    children: [
      { path: "/confirm-send-as", element: <ConfirmSendAs /> }
    ],
  },
  {
    element: <InitialLayout />,
    children: [
      { path: "/signup", element: <Signup /> },
      // /login only ever rendered a placeholder component — a blank page with no way to sign in. The real
      // sign-in flow is /signin (routes/SignIn.js), so keep the path (old links and bookmarks still land
      // somewhere useful) and redirect it there instead of removing it.
      { path: "/login", element: <Navigate to="/signin" replace /> },
      { path: "/", element: <WalletConnect /> },
    ]
  },
  {
    element: withoutLayout(),
    children: [
      { path: "/signin", element: <SignIn /> },
      { path: "/securemail/:id", element: <SecureMail /> }
    ]
  }
], {
  basename: "/app"
});

// ErrorBoundary wraps the router, not the other way round: a throw anywhere in any route unmounts the whole
// React tree by default, which is what turned every render bug into a blank white page. Keep it OUTSIDE
// RouterProvider so it survives the crashed route, and INSIDE Provider/ConfigProvider so its fallback still
// gets the theme.
createRoot(document.getElementById("root")).render(
  <Provider store={store}>
    <ConfigProvider theme={dmailTheme}>
      <ErrorBoundary>
        <RouterProvider router={router} />
      </ErrorBoundary>
    </ConfigProvider>
  </Provider>
);
